import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Badge,
  Skeleton,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from "@repo/ui";
import {
  Pill,
  Building2,
  Package,
  ArrowDownToLine,
  ArrowUpFromLine,
  Activity,
  ClipboardCheck,
  ExternalLink,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { api, ApiClientError } from "@/lib/api/client";
import type { InventoryStatus } from "@/lib/types/inventory";
import type { ActivityTransaction } from "@/lib/types/activity";
import {
  getStatusConfig,
  getDEAScheduleNumeral,
} from "@/lib/utils/inventory-format";
import { handlePermissionError } from "@/lib/utils/handle-permission-error";
import { useApproval } from "@/context/approval-context";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3005";

// ── Helpers ──────────────────────────────────────────────────────────────────

function getTypeColors(type: string) {
  switch (type) {
    case "receipt":
      return {
        dot: "bg-emerald-500",
        text: "text-emerald-600",
        bg: "bg-emerald-500/8",
      };
    case "dispense":
      return {
        dot: "bg-primary",
        text: "text-foreground",
        bg: "bg-primary/8",
      };
    case "adjustment":
      return {
        dot: "bg-amber-500",
        text: "text-amber-600",
        bg: "bg-amber-500/8",
      };
    default:
      return {
        dot: "bg-muted-foreground",
        text: "text-muted-foreground",
        bg: "bg-muted/30",
      };
  }
}

function getTypeLabel(type: string) {
  return (
    { receipt: "Received", dispense: "Dispensed", adjustment: "Adjusted" }[
      type
    ] ?? type
  );
}

// ── Compact Activity Timeline ────────────────────────────────────────────────

function CompactTimeline({
  transactions,
  loading,
}: {
  transactions: ActivityTransaction[];
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-start gap-3">
            <Skeleton className="h-6 w-6 rounded-full shrink-0 mt-0.5" />
            <div className="flex-1 space-y-1.5 pt-0.5">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-2.5 w-28" />
            </div>
            <Skeleton className="h-2.5 w-14 mt-0.5" />
          </div>
        ))}
      </div>
    );
  }

  if (!transactions.length) {
    return (
      <div className="flex flex-col items-center justify-center py-14 text-center">
        <div className="rounded-full bg-muted p-3 mb-3">
          <Activity className="h-5 w-5 text-muted-foreground" />
        </div>
        <p className="text-sm font-medium text-muted-foreground">
          No activity yet
        </p>
        <p className="text-xs text-muted-foreground mt-1 max-w-[180px]">
          Receipts and dispenses will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Vertical connecting line */}
      <div className="absolute left-[12px] top-4 bottom-4 w-px bg-border/70 pointer-events-none" />

      <div className="space-y-1">
        {transactions.map((t, i) => {
          const colors = getTypeColors(t.transaction_type);
          const isPositive = t.quantity > 0;
          const dateObj = new Date(t.transaction_date);
          const dateStr = format(dateObj, "MMM d");
          const timeStr = format(dateObj, "h:mm a");

          return (
            <div key={i} className="relative flex items-start gap-3 py-1">
              {/* Dot node */}
              <div
                className={`relative z-10 flex items-center justify-center h-[25px] w-[25px] rounded-full ${colors.bg} border border-border/60 shrink-0 mt-0.5`}
              >
                <div className={`h-2 w-2 rounded-full ${colors.dot}`} />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 pt-0.5">
                <div className="flex items-baseline justify-between gap-2">
                  <span className={`text-xs font-semibold ${colors.text}`}>
                    {getTypeLabel(t.transaction_type)}
                  </span>
                  <span
                    className={`text-xs font-semibold tabular-nums shrink-0 ${
                      isPositive ? "text-emerald-600" : "text-foreground"
                    }`}
                  >
                    {isPositive ? "+" : ""}
                    {t.quantity}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <p className="text-[11px] text-muted-foreground truncate">
                    {t.created_by_name || "—"}
                    {t.stock_after_transaction != null && (
                      <span className="opacity-60">
                        {" "}
                        · stock: {t.stock_after_transaction}
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] text-muted-foreground shrink-0 whitespace-nowrap">
                    {dateStr} · {timeStr}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface DrugDetailsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ndc: string;
  mode?: "view" | "edit";
  inventoryItem?: {
    id: string;
    item_name: string;
    drug_type: string;
  };
  stockInfo?: {
    stock_calculated: number;
    status: "in_stock" | "reorder_needed" | "sold_out";
  };
  onEditSuccess?: () => void;
  onReceive?: (ndc: string, itemName: string) => void;
  onDispense?: (ndc: string, itemName: string) => void;
}

interface DrugDetails {
  product_ndc: string;
  item_name: string;
  generic_name: string;
  brand_name: string;
  brand_name_base?: string;
  labeler_name: string;
  dea_schedule: string;
  dosage_form: string;
  route: string[];
  active_ingredients: Array<{ name: string; strength: string }>;
  packaging?: string[];
  marketing_category?: string;
  application_number?: string;
  nda?: string | null;
  anda?: string | null;
  bla?: string | null;
  pharm_class?: string[];
  product_type?: string;
  marketing_start_date?: string;
  color?: string | null;
  color_text?: string | null;
  shape?: string | null;
  shape_text?: string | null;
  imprint_code?: string | null;
  size?: string | null;
  score?: string | null;
}

// ── Section header helper ────────────────────────────────────────────────────

function SectionLabel({
  icon: Icon,
  label,
}: {
  icon: typeof Pill;
  label: string;
}) {
  return (
    <div className="flex items-center gap-1.5 mb-2">
      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function DrugDetailsDrawer({
  open,
  onOpenChange,
  ndc,
  mode = "view",
  inventoryItem,
  stockInfo,
  onEditSuccess,
  onReceive,
  onDispense,
}: DrugDetailsDrawerProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { requestApproval } = useApproval();

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [drugDetails, setDrugDetails] = React.useState<DrugDetails | null>(
    null
  );
  const [isEditing, setIsEditing] = React.useState(false);
  const [editItemName, setEditItemName] = React.useState("");
  const [editDrugType, setEditDrugType] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);

  const [fetchedStock, setFetchedStock] = React.useState<{
    stock_calculated: number;
    status: "in_stock" | "reorder_needed" | "sold_out";
  } | null>(null);
  const [stockLoading, setStockLoading] = React.useState(false);
  const [lastAuditDate, setLastAuditDate] = React.useState<
    string | null | undefined
  >(undefined);

  const [recentActivity, setRecentActivity] = React.useState<
    ActivityTransaction[]
  >([]);
  const [activityLoading, setActivityLoading] = React.useState(false);
  const [fetchedInventoryId, setFetchedInventoryId] = React.useState<
    string | null
  >(null);

  // ── Activity fetch ──────────────────────────────────────────────────────

  const fetchRecentActivity = React.useCallback(async () => {
    setActivityLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/activity?search=${encodeURIComponent(ndc)}&pageSize=15`,
        { credentials: "include" }
      );
      if (res.ok) {
        const result = await res.json();
        setRecentActivity(result.data?.transactions ?? []);
      }
    } catch {
      // silently fail — activity is supplementary
    } finally {
      setActivityLoading(false);
    }
  }, [ndc]);

  // ── Stock + audit fetch ─────────────────────────────────────────────────

  const fetchInventoryStock = React.useCallback(async () => {
    setStockLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/inventory?search=${encodeURIComponent(ndc)}&pageSize=1`,
        { credentials: "include" }
      );
      if (response.ok) {
        const result = await response.json();
        const items = result.data?.inventory ?? result.inventory;
        if (items && items.length > 0) {
          const item = items[0];
          setFetchedStock({
            stock_calculated: item.stock_calculated ?? 0,
            status: item.status ?? "sold_out",
          });
          const inventoryId: string | undefined = item.id;
          if (inventoryId) {
            setFetchedInventoryId(inventoryId);
            try {
              const auditResponse = await fetch(
                `${API_URL}/inventory/${inventoryId}/last-audit`,
                { credentials: "include" }
              );
              if (auditResponse.ok) {
                const auditResult = await auditResponse.json();
                setLastAuditDate(auditResult.lastAuditDate ?? null);
              } else {
                setLastAuditDate(null);
              }
            } catch {
              setLastAuditDate(null);
            }
          } else {
            setLastAuditDate(null);
          }
        } else {
          setFetchedStock(null);
          setLastAuditDate(null);
        }
      } else {
        setFetchedStock(null);
        setLastAuditDate(null);
      }
    } catch {
      setFetchedStock(null);
      setLastAuditDate(null);
    } finally {
      setStockLoading(false);
    }
  }, [ndc]);

  // ── Drug details fetch ──────────────────────────────────────────────────

  const fetchDrugDetails = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_URL}/ndc/lookup?ndc=${encodeURIComponent(ndc)}`,
        { credentials: "include" }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch drug details");
      }
      const result = await response.json();
      if (result.success && result.data) {
        const drugData = result.data;
        const normalizedNDC = ndc
          .split("-")
          .slice(0, 2)
          .map((seg) => parseInt(seg, 10).toString())
          .join("-");
        setDrugDetails({
          product_ndc: normalizedNDC,
          item_name: drugData.item_name || "",
          generic_name: drugData.generic_name || "",
          brand_name: drugData.brand_name || "",
          labeler_name: drugData.labeler_name || "",
          dea_schedule: drugData.dea_schedule || "",
          dosage_form: drugData.dosage_form || "",
          route: drugData.route || [],
          active_ingredients: drugData.active_ingredients || [],
          packaging: drugData.packaging || [],
          marketing_category: drugData.marketing_category,
          application_number: drugData.application_number,
          nda: drugData.nda,
          anda: drugData.anda,
          bla: drugData.bla,
          pharm_class: drugData.pharm_class,
          product_type: drugData.product_type,
          marketing_start_date: drugData.marketing_start_date,
          color: drugData.color,
          color_text: drugData.color_text,
          shape: drugData.shape,
          shape_text: drugData.shape_text,
          imprint_code: drugData.imprint_code,
          size: drugData.size,
          score: drugData.score,
        });
      } else {
        throw new Error("No drug details found");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  }, [ndc]);

  // ── Effects ─────────────────────────────────────────────────────────────

  React.useEffect(() => {
    if (open && ndc) {
      fetchDrugDetails();
      fetchInventoryStock();
      fetchRecentActivity();
      setIsEditing(mode === "edit");
      if (mode === "edit" && inventoryItem) {
        setEditItemName(inventoryItem.item_name);
        setEditDrugType(inventoryItem.drug_type);
      }
    } else {
      setIsEditing(false);
      setEditItemName("");
      setEditDrugType("");
      setFetchedStock(null);
      setLastAuditDate(undefined);
      setRecentActivity([]);
      setFetchedInventoryId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    ndc,
    mode,
    fetchDrugDetails,
    fetchInventoryStock,
    fetchRecentActivity,
  ]);

  // ── Edit handlers ────────────────────────────────────────────────────────

  const handleSaveEdit = async (approvalToken?: string) => {
    if (!inventoryItem) return;
    setIsSaving(true);
    try {
      await api.patch(`/inventory/${inventoryItem.id}`, {
        drug_name: editItemName,
        drug_type: editDrugType,
        ...(approvalToken ? { approvalToken } : {}),
      });
      toast({
        title: "Item Updated",
        description: "Inventory item has been updated successfully.",
        variant: "success",
      });
      onEditSuccess?.();
      onOpenChange(false);
    } catch (error) {
      if (error instanceof ApiClientError) {
        if (
          handlePermissionError(error, {
            requestApproval,
            toast,
            retryFn: async (token) => handleSaveEdit(token),
            actionDescription: `Edit inventory item ${inventoryItem.item_name}`,
          })
        )
          return;
      }
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to update item",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (inventoryItem) {
      setEditItemName(inventoryItem.item_name);
      setEditDrugType(inventoryItem.drug_type);
    }
    setIsEditing(false);
    onOpenChange(false);
  };

  // ── Derived ──────────────────────────────────────────────────────────────

  const resolvedStock = stockInfo ?? fetchedStock;
  const stockStatusConfig = resolvedStock
    ? getStatusConfig(resolvedStock.status as InventoryStatus)
    : null;

  const scheduleNumeral = drugDetails
    ? getDEAScheduleNumeral(drugDetails.dea_schedule)
    : null;

  const activeInventoryId = fetchedInventoryId ?? inventoryItem?.id ?? null;

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-4xl flex flex-col p-0 gap-0 overflow-hidden">
        {/* ── Header ─────────────────────────────────────────────────── */}
        <SheetHeader className="px-6 pt-4 pb-3 border-b shrink-0 space-y-0">
          <SheetTitle className="text-base font-semibold">
            {isEditing ? "Edit Drug" : "Drug Information"}
          </SheetTitle>
          <SheetDescription className="text-xs">
            {isEditing
              ? "Update drug name and schedule type"
              : "Data from OpenFDA Drug Database"}
          </SheetDescription>
        </SheetHeader>

        {/* ── Edit Mode ──────────────────────────────────────────────── */}
        {isEditing && inventoryItem && (
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="edit-ndc" className="text-xs font-medium">
                NDC Code
              </Label>
              <Input
                id="edit-ndc"
                value={ndc}
                disabled
                className="bg-muted font-mono text-sm h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-item-name" className="text-xs font-medium">
                Drug Name
              </Label>
              <Input
                id="edit-item-name"
                value={editItemName}
                onChange={(e) => setEditItemName(e.target.value)}
                placeholder="Enter medication name"
                className="h-9 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-drug-type" className="text-xs font-medium">
                Schedule Type
              </Label>
              <Select value={editDrugType} onValueChange={setEditDrugType}>
                <SelectTrigger id="edit-drug-type" className="h-9 text-sm">
                  <SelectValue placeholder="Select schedule type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="schedule_ii">Schedule II</SelectItem>
                  <SelectItem value="schedule_iii">Schedule III</SelectItem>
                  <SelectItem value="schedule_iv">Schedule IV</SelectItem>
                  <SelectItem value="schedule_v">Schedule V</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2.5 pt-2">
              <Button
                onClick={() => handleSaveEdit()}
                disabled={isSaving || !editItemName || !editDrugType}
                className="flex-1 h-9"
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
              <Button
                variant="outline"
                onClick={handleCancelEdit}
                disabled={isSaving}
                className="h-9"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* ── View Mode: Two-column layout ───────────────────────────── */}
        {!isEditing && (
          <div className="flex flex-1 min-h-0 overflow-hidden">
            {/* ── LEFT: Drug info ─────────────────────────────────── */}
            <div className="w-[52%] border-r flex flex-col overflow-y-auto">
              {/* Loading skeleton */}
              {loading && (
                <div className="p-5 space-y-4">
                  <div className="space-y-2">
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                    <div className="flex gap-1.5">
                      <Skeleton className="h-5 w-16" />
                      <Skeleton className="h-5 w-20" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Skeleton className="h-16 rounded-lg" />
                    <Skeleton className="h-16 rounded-lg" />
                    <Skeleton className="h-16 rounded-lg" />
                  </div>
                  <Skeleton className="h-11 rounded-lg" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-9 w-full" />
                    <Skeleton className="h-9 w-full" />
                  </div>
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-9 w-full" />
                  </div>
                </div>
              )}

              {/* Error state */}
              {error && !loading && (
                <div className="p-5 text-center">
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              {/* Drug details */}
              {drugDetails && !loading && (
                <>
                  {/* Hero area */}
                  <div className="px-5 pt-5 pb-4 border-b">
                    <div className="flex items-start gap-3">
                      <div className="rounded-xl bg-primary/10 p-2.5 shrink-0">
                        <Pill className="h-4 w-4 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1 mb-1.5">
                          {scheduleNumeral ? (
                            <Badge
                              variant="destructive"
                              className="text-[10px] px-1.5 py-0 font-bold h-4"
                            >
                              {scheduleNumeral}
                            </Badge>
                          ) : (
                            <Badge
                              variant="secondary"
                              className="text-[10px] px-1.5 py-0 h-4"
                            >
                              Non-Controlled
                            </Badge>
                          )}
                          {drugDetails.dosage_form && (
                            <Badge
                              variant="secondary"
                              className="text-[10px] px-1.5 py-0 h-4"
                            >
                              {drugDetails.dosage_form}
                            </Badge>
                          )}
                        </div>
                        <h3 className="text-sm font-semibold leading-snug text-foreground">
                          {drugDetails.item_name}
                        </h3>
                        <p className="text-[11px] text-muted-foreground mt-1 font-mono">
                          {drugDetails.product_ndc}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Stock mini-cards */}
                  <div className="px-4 py-3 border-b">
                    {stockLoading && !resolvedStock ? (
                      <div className="grid grid-cols-3 gap-2">
                        {[0, 1, 2].map((i) => (
                          <Skeleton key={i} className="h-14 rounded-lg" />
                        ))}
                      </div>
                    ) : resolvedStock && stockStatusConfig ? (
                      <div className="grid grid-cols-3 gap-2">
                        <div className="rounded-lg border bg-surface p-2.5 space-y-0.5">
                          <p className="text-[10px] font-medium text-muted-foreground">
                            Stock
                          </p>
                          <p className="text-xl font-bold tabular-nums leading-none">
                            {resolvedStock.stock_calculated}
                          </p>
                        </div>
                        <div className="rounded-lg border bg-surface p-2.5 space-y-0.5">
                          <p className="text-[10px] font-medium text-muted-foreground">
                            Status
                          </p>
                          <Badge
                            variant="outline"
                            className={`text-[10px] px-1.5 h-5 font-medium ${stockStatusConfig.className}`}
                          >
                            {stockStatusConfig.label}
                          </Badge>
                        </div>
                        <div className="rounded-lg border bg-surface p-2.5 space-y-0.5">
                          <div className="flex items-center gap-1">
                            <ClipboardCheck className="h-2.5 w-2.5 text-muted-foreground" />
                            <p className="text-[10px] font-medium text-muted-foreground">
                              Last Audit
                            </p>
                          </div>
                          {lastAuditDate === undefined ? (
                            <Skeleton className="h-4 w-14" />
                          ) : lastAuditDate ? (
                            <p className="text-xs font-semibold leading-none mt-1">
                              {format(parseISO(lastAuditDate), "MMM d, yyyy")}
                            </p>
                          ) : (
                            <p className="text-xs text-muted-foreground italic leading-none mt-1">
                              Never
                            </p>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  {/* Drug info content */}
                  <div className="px-5 py-4 space-y-4 flex-1">
                    {/* Manufacturer */}
                    <div className="flex items-center gap-2.5">
                      <div className="rounded-lg bg-muted/60 p-1.5 shrink-0">
                        <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] font-medium text-muted-foreground">
                          Manufacturer
                        </p>
                        <p className="text-xs font-semibold truncate">
                          {drugDetails.labeler_name}
                        </p>
                      </div>
                    </div>

                    {/* Active Ingredients */}
                    {drugDetails.active_ingredients.length > 0 && (
                      <div>
                        <SectionLabel icon={Pill} label="Active Ingredients" />
                        <div className="space-y-1.5">
                          {drugDetails.active_ingredients.map(
                            (ingredient, idx) => (
                              <div
                                key={idx}
                                className="flex justify-between items-center px-2.5 py-2 rounded-lg border bg-surface"
                              >
                                <span className="text-xs font-medium truncate mr-2">
                                  {ingredient.name}
                                </span>
                                <Badge
                                  variant="secondary"
                                  className="text-[10px] px-1.5 h-5 font-mono shrink-0"
                                >
                                  {ingredient.strength}
                                </Badge>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}

                    {/* Packaging */}
                    {drugDetails.packaging &&
                      drugDetails.packaging.length > 0 && (
                        <div>
                          <SectionLabel
                            icon={Package}
                            label="Packaging Options"
                          />
                          <div className="space-y-1.5 max-h-[150px] overflow-y-auto pr-1">
                            {drugDetails.packaging.map((pkgString, idx) => {
                              const match = pkgString.match(
                                /^(.+?)\s*\(([^)]+)\)$/
                              );
                              const description = match
                                ? match[1].trim()
                                : pkgString;
                              const packageNdc = match ? match[2].trim() : "";
                              return (
                                <div
                                  key={idx}
                                  className="flex items-start gap-2 px-2.5 py-2 rounded-lg border bg-surface"
                                >
                                  {packageNdc && (
                                    <Badge
                                      variant="outline"
                                      className="text-[9px] px-1 h-4 font-mono shrink-0 mt-0.5"
                                    >
                                      {packageNdc}
                                    </Badge>
                                  )}
                                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                                    {description}
                                  </p>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                    {/* Physical Attributes */}
                    {(drugDetails.color_text ||
                      drugDetails.shape_text ||
                      drugDetails.imprint_code ||
                      drugDetails.size ||
                      drugDetails.score) && (
                      <div>
                        <SectionLabel icon={Pill} label="Physical Attributes" />
                        <div className="grid grid-cols-2 gap-1.5">
                          {[
                            {
                              label: "Color",
                              value: drugDetails.color_text,
                            },
                            {
                              label: "Shape",
                              value: drugDetails.shape_text,
                            },
                            { label: "Size", value: drugDetails.size },
                            { label: "Score", value: drugDetails.score },
                          ]
                            .filter((a) => a.value)
                            .map((attr) => (
                              <div
                                key={attr.label}
                                className="px-2 py-1.5 rounded-lg border bg-surface"
                              >
                                <p className="text-[10px] text-muted-foreground">
                                  {attr.label}
                                </p>
                                <p className="text-xs font-semibold">
                                  {attr.value}
                                </p>
                              </div>
                            ))}
                          {drugDetails.imprint_code && (
                            <div className="col-span-2 px-2 py-1.5 rounded-lg border bg-surface">
                              <p className="text-[10px] text-muted-foreground">
                                Imprint
                              </p>
                              <p className="text-xs font-semibold font-mono">
                                {drugDetails.imprint_code}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Routes & Pharm Class */}
                    {((drugDetails.route && drugDetails.route.length > 0) ||
                      (drugDetails.pharm_class &&
                        drugDetails.pharm_class.length > 0)) && (
                      <div className="space-y-3">
                        {drugDetails.route && drugDetails.route.length > 0 && (
                          <div>
                            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                              Routes
                            </p>
                            <div className="flex gap-1.5 flex-wrap">
                              {drugDetails.route.map((r, idx) => (
                                <Badge
                                  key={idx}
                                  variant="secondary"
                                  className="text-[10px] px-1.5 h-5"
                                >
                                  {r}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {drugDetails.pharm_class &&
                          drugDetails.pharm_class.length > 0 && (
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                                Pharmacological Class
                              </p>
                              <div className="flex gap-1.5 flex-wrap">
                                {drugDetails.pharm_class.map((pc, idx) => (
                                  <Badge
                                    key={idx}
                                    variant="outline"
                                    className="text-[10px] px-1.5 h-5"
                                  >
                                    {pc}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* ── RIGHT: Activity Timeline ────────────────────────── */}
            <div className="flex-1 flex flex-col min-w-0">
              {/* Activity header */}
              <div className="px-5 pt-4 pb-3 border-b shrink-0 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold">Recent Activity</p>
                  <p className="text-xs text-muted-foreground">
                    Recent transactions for this drug
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs gap-1 text-muted-foreground hover:text-foreground px-2"
                  onClick={() => {
                    navigate({
                      to: `/activity?search=${encodeURIComponent(ndc)}`,
                    });
                    onOpenChange(false);
                  }}
                >
                  View all
                  <ExternalLink className="h-3 w-3" />
                </Button>
              </div>

              {/* Timeline content */}
              <div className="flex-1 overflow-y-auto px-5 py-4">
                <CompactTimeline
                  transactions={recentActivity}
                  loading={activityLoading}
                />
              </div>
            </div>
          </div>
        )}

        {/* ── Action Bar ──────────────────────────────────────────────── */}
        {!isEditing && drugDetails && (
          <div className="border-t px-5 py-3 shrink-0 flex items-center gap-2 bg-surface">
            <Button
              className="flex-1 h-9 gap-2 text-sm bg-primary hover:bg-primary-hover"
              onClick={() => {
                if (onReceive) {
                  onReceive(ndc, drugDetails.item_name);
                  onOpenChange(false);
                } else {
                  navigate({
                    to: `/receipts?action=create&ndc=${encodeURIComponent(ndc)}&itemName=${encodeURIComponent(drugDetails.item_name)}`,
                  });
                  onOpenChange(false);
                }
              }}
            >
              <ArrowUpFromLine className="h-3.5 w-3.5" />
              Receive
            </Button>
            <Button
              variant="secondary"
              className="flex-1 h-9 gap-2 text-sm"
              onClick={() => {
                if (onDispense) {
                  onDispense(ndc, drugDetails.item_name);
                  onOpenChange(false);
                } else {
                  navigate({
                    to: `/dispenses?action=create&ndc=${encodeURIComponent(ndc)}&itemName=${encodeURIComponent(drugDetails.item_name)}`,
                  });
                  onOpenChange(false);
                }
              }}
            >
              <ArrowDownToLine className="h-3.5 w-3.5" />
              Dispense
            </Button>
            {activeInventoryId && (
              <Button
                variant="ghost"
                className="h-9 gap-1.5 text-sm text-muted-foreground hover:text-foreground px-3 shrink-0"
                onClick={() => {
                  navigate({
                    to: `/activity?search=${encodeURIComponent(ndc)}`,
                  });
                  onOpenChange(false);
                }}
              >
                <Activity className="h-3.5 w-3.5" />
                Activity
              </Button>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
